import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

/**
 * The entity whose detail the user currently has open (a talent drawer today).
 * Published by the single `TalentDetailDispatcher` call site and read by the
 * layout-mounted Companion, so the Protector can resolve "this talent" without
 * the sponsor naming them. Only the id + type travel; the name is loaded
 * server-side via the (pipeline-gated) tool result.
 */
export interface CoPilotPageEntity {
  id: string;
  type: 'talent';
  /** The journey of the open drawer — the relationship the Companion acts on (X8). */
  journeyId: string;
}

interface CoPilotSidebarContextProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  pageEntity: CoPilotPageEntity | null;
  setPageEntity: (entity: CoPilotPageEntity | null) => void;
}

const CoPilotSidebarContext = createContext<CoPilotSidebarContextProps | null>(null);

export function CoPilotSidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pageEntity, setPageEntityState] = useState<CoPilotPageEntity | null>(null);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  /**
   * Ignore a set that says the same thing.
   *
   * Callers naturally write `setPageEntity({ id, type: 'talent' })`, a NEW object
   * each call, so `Object.is` never matches and React re-renders every time even
   * though nothing changed. Comparing by VALUE makes a repeat set a genuine no-op
   * — which is what stops a caller's effect from feeding itself.
   */
  const setPageEntity = useCallback((next: CoPilotPageEntity | null) => {
    setPageEntityState((prev) => {
      if (prev === next) return prev;
      if (
        prev &&
        next &&
        prev.id === next.id &&
        prev.type === next.type &&
        prev.journeyId === next.journeyId
      )
        return prev;
      return next;
    });
  }, []);

  /**
   * MEMOISED — a fresh object here re-renders every consumer on every provider
   * render, and any consumer effect that (correctly) depends on the context
   * value then re-fires forever. That is exactly what happened: the talent
   * drawer published its open talent from an effect keyed on this value, the
   * set re-rendered the provider, the new value re-triggered the effect, and the
   * whole drawer spun ~500 times per open. It surfaced as a crash only where a
   * chart happened to be mounted (Recharts throws on the churn), which sent the
   * hunt to the wrong place entirely.
   */
  const value = useMemo(
    () => ({ isOpen, setIsOpen, toggle, pageEntity, setPageEntity }),
    [isOpen, toggle, pageEntity, setPageEntity]
  );

  return <CoPilotSidebarContext.Provider value={value}>{children}</CoPilotSidebarContext.Provider>;
}

export function useCoPilotSidebar() {
  const context = useContext(CoPilotSidebarContext);
  if (!context) {
    throw new Error('useCoPilotSidebar must be used within CoPilotSidebarProvider');
  }
  return context;
}

/**
 * Non-throwing accessor for components (like the shared TalentDetailDispatcher)
 * that MAY render outside the provider (e.g. in isolated unit tests). Returns
 * null instead of throwing so page-entity publishing degrades to a no-op.
 */
export function useCoPilotSidebarOptional() {
  return useContext(CoPilotSidebarContext);
}
